import { Link } from "react-router-dom";
import type { MouseEvent } from "react";
import type { Film } from "@/mocks/films";
import { getCategoryLabel } from "@/lib/films";
import { useFavorites } from "@/hooks/useFavorites";

interface FilmCardProps {
  film: Film;
  compact?: boolean;
}

export default function FilmCard({ film, compact = false }: FilmCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(film.id);

  const handleFavorite = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    toggleFavorite(film.id);
  };

  return (
    <Link
      to={`/detail/${film.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={film.poster}
          alt={`${film.title}海报`}
          title={`${film.title} 剧情介绍与角色分析`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-950/75 px-2 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
          {getCategoryLabel(film.category)}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={favorited ? "取消收藏" : "收藏"}
          className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md backdrop-blur-sm transition-colors ${
            favorited ? "bg-accent-500 text-background-50" : "bg-background-50/85 text-foreground-700 hover:text-accent-600"
          }`}
        >
          <i className={favorited ? "ri-star-fill text-base" : "ri-star-line text-base"} />
        </button>
        <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-background-50/90 px-2 py-0.5 text-xs font-semibold text-accent-700">
          <i className="ri-star-smile-line text-sm" />
          {film.rating.toFixed(1)}
        </span>
      </div>

      <div className={compact ? "flex flex-1 flex-col gap-1 p-3" : "flex flex-1 flex-col gap-2 p-4"}>
        <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-1">{film.title}</h3>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
          <span>{film.year}</span>
          <span className="text-foreground-300">·</span>
          <span>{film.region}</span>
          <span className="text-foreground-300">·</span>
          <span className="truncate">{film.genres.join(" / ")}</span>
        </p>
        {!compact && <p className="text-sm leading-relaxed text-foreground-600 line-clamp-2">{film.oneLine}</p>}
        <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
          {film.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}